import { apiClient } from './api-client';

export type CustomerStatus = 'ACTIVE' | 'INACTIVE';
export type DeletedReason = 'DIRECT' | 'CASCADE' | null;

// ── Entities ────────────────────────────────────────────────────────────────

export interface Customer {
  id: string;
  tenantId: string;
  name: string;
  customerCode: string | null;
  organizationName: string | null;
  username: string | null;
  mustChangePassword: boolean;
  passwordChangedAt: string | null;
  lastLoginAt: string | null;
  email: string | null;
  phone: string | null;
  alternatePhone: string | null;
  gstNumber: string | null;
  address: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postalCode: string | null;
  website: string | null;
  industry: string | null;
  notes: string | null;
  isActive: boolean;
  status: CustomerStatus;
  locationsCount?: number;
  equipmentCount?: number;
  /** Primary contact full name; null when the customer has no primary contact. */
  primaryContactName?: string | null;
  deletedAt: string | null;
  deletedByUserId: string | null;
  deletedReason: DeletedReason;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerLocation {
  id: string;
  tenantId: string;
  customerId: string;
  name: string;
  address: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postalCode: string | null;
  phone: string | null;
  alternatePhone: string | null;
  email: string | null;
  amcDetails: string | null;
  isPrimary: boolean;
  notes: string | null;
  isActive: boolean;
  deletedAt: string | null;
  deletedReason: DeletedReason;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerContact {
  id: string;
  tenantId: string;
  customerId: string;
  customerLocationId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  alternatePhone: string | null;
  title: string | null;
  designation: string | null;
  department: string | null;
  isPrimary: boolean;
  notes: string | null;
  isActive: boolean;
  deletedAt: string | null;
  deletedReason: DeletedReason;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerListResponse {
  items: Customer[];
  page: number;
  pageSize: number;
  total: number;
}

export interface CustomerStats {
  locations: number;
  contacts: number;
  equipment: number;
  openIntakes: number;
  serviceRequests: 'COMING_SOON';
  amcContracts: 'COMING_SOON';
}

export interface FieldValidation {
  available: boolean;
  duplicate: boolean;
  existingName: string | null;
  source: 'USER' | 'CUSTOMER' | null;
  existingIdentifier: string | null;
  warning: boolean;
}

export interface CaptchaChallenge {
  token: string;
  question: string;
  expiresInSeconds: number;
}

// ── Payloads ──────────────────────────────────────────────────────────────────

export interface CreateCustomerPayload {
  name: string;
  organizationName: string;
  customerCode?: string;
  username?: string;
  temporaryPassword?: string;
  email?: string;
  phone?: string;
  alternatePhone?: string;
  gstNumber?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  website?: string;
  industry?: string;
  notes?: string;
  captchaToken: string;
  captchaAnswer: string;
}

export type UpdateCustomerPayload = Partial<
  Omit<CreateCustomerPayload, 'captchaToken' | 'captchaAnswer'>
> & {
  isActive?: boolean;
};

export interface CustomerCreatedResponse extends Customer {
  temporaryPassword: string;
}

export interface CredentialResetResponse {
  username: string | null;
  temporaryPassword: string;
}

export interface CreateLocationPayload {
  name: string;
  addressLine1: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  phone?: string;
  alternatePhone?: string;
  email?: string;
  amcDetails?: string;
  isPrimary?: boolean;
  notes?: string;
}
export type UpdateLocationPayload = Partial<CreateLocationPayload> & { isActive?: boolean };

export interface CreateContactPayload {
  firstName: string;
  lastName: string;
  phone: string;
  alternatePhone?: string;
  email?: string;
  designation?: string;
  department?: string;
  title?: string;
  isPrimary?: boolean;
  notes?: string;
}
export type UpdateContactPayload = Partial<CreateContactPayload> & { isActive?: boolean };

// ── Service ─────────────────────────────────────────────────────────────────

const base = '/customers';

export const customerService = {
  // Customers
  list: (params: {
    page?: number;
    pageSize?: number;
    search?: string;
  }): Promise<CustomerListResponse> =>
    apiClient.get<CustomerListResponse>(base, { params }).then((r) => r.data),

  listDeleted: (params: {
    page?: number;
    pageSize?: number;
    search?: string;
  }): Promise<CustomerListResponse> =>
    apiClient.get<CustomerListResponse>(`${base}/deleted`, { params }).then((r) => r.data),

  get: (id: string): Promise<Customer> =>
    apiClient.get<Customer>(`${base}/${id}`).then((r) => r.data),

  stats: (id: string): Promise<CustomerStats> =>
    apiClient.get<CustomerStats>(`${base}/${id}/stats`).then((r) => r.data),

  create: (payload: CreateCustomerPayload): Promise<CustomerCreatedResponse> =>
    apiClient.post<CustomerCreatedResponse>(base, payload).then((r) => r.data),

  update: (id: string, payload: UpdateCustomerPayload): Promise<Customer> =>
    apiClient.patch<Customer>(`${base}/${id}`, payload).then((r) => r.data),

  remove: (id: string): Promise<void> => apiClient.delete(`${base}/${id}`).then(() => undefined),

  restore: (id: string): Promise<Customer> =>
    apiClient.post<Customer>(`${base}/${id}/restore`).then((r) => r.data),

  resetPassword: (id: string): Promise<CredentialResetResponse> =>
    apiClient.post<CredentialResetResponse>(`${base}/${id}/reset-password`).then((r) => r.data),

  // Generation
  generateCode: (): Promise<string> =>
    apiClient
      .get<{ customerCode: string }>(`${base}/generate/code`)
      .then((r) => r.data.customerCode),
  generateUsername: (organizationName: string): Promise<string> =>
    apiClient
      .get<{ username: string }>(`${base}/generate/username`, { params: { organizationName } })
      .then((r) => r.data.username),
  generatePassword: (): Promise<string> =>
    apiClient.get<{ password: string }>(`${base}/generate/password`).then((r) => r.data.password),

  // Validation
  validate: (
    field: 'customer-code' | 'email' | 'phone' | 'username' | 'gst',
    value: string,
    excludeId?: string,
  ): Promise<FieldValidation> =>
    apiClient
      .get<FieldValidation>(`${base}/validate/${field}`, { params: { value, excludeId } })
      .then((r) => r.data),

  // Captcha
  captcha: (): Promise<CaptchaChallenge> =>
    apiClient.get<CaptchaChallenge>('/captcha').then((r) => r.data),

  // Locations
  listLocations: (customerId: string): Promise<CustomerLocation[]> =>
    apiClient.get<CustomerLocation[]>(`${base}/${customerId}/locations`).then((r) => r.data),
  listDeletedLocations: (customerId: string): Promise<CustomerLocation[]> =>
    apiClient
      .get<CustomerLocation[]>(`${base}/${customerId}/locations/deleted`)
      .then((r) => r.data),
  createLocation: (customerId: string, payload: CreateLocationPayload): Promise<CustomerLocation> =>
    apiClient
      .post<CustomerLocation>(`${base}/${customerId}/locations`, payload)
      .then((r) => r.data),
  updateLocation: (
    customerId: string,
    id: string,
    payload: UpdateLocationPayload,
  ): Promise<CustomerLocation> =>
    apiClient
      .patch<CustomerLocation>(`${base}/${customerId}/locations/${id}`, payload)
      .then((r) => r.data),
  removeLocation: (customerId: string, id: string): Promise<void> =>
    apiClient.delete(`${base}/${customerId}/locations/${id}`).then(() => undefined),
  restoreLocation: (customerId: string, id: string): Promise<CustomerLocation> =>
    apiClient
      .post<CustomerLocation>(`${base}/${customerId}/locations/${id}/restore`)
      .then((r) => r.data),

  // Contacts (created/listed UNDER a location)
  listContacts: (customerId: string): Promise<CustomerContact[]> =>
    apiClient.get<CustomerContact[]>(`${base}/${customerId}/contacts`).then((r) => r.data),
  listDeletedContacts: (customerId: string): Promise<CustomerContact[]> =>
    apiClient.get<CustomerContact[]>(`${base}/${customerId}/contacts/deleted`).then((r) => r.data),
  listLocationContacts: (customerId: string, locationId: string): Promise<CustomerContact[]> =>
    apiClient
      .get<CustomerContact[]>(`${base}/${customerId}/locations/${locationId}/contacts`)
      .then((r) => r.data),
  createContact: (
    customerId: string,
    locationId: string,
    payload: CreateContactPayload,
  ): Promise<CustomerContact> =>
    apiClient
      .post<CustomerContact>(`${base}/${customerId}/locations/${locationId}/contacts`, payload)
      .then((r) => r.data),
  updateContact: (
    customerId: string,
    id: string,
    payload: UpdateContactPayload,
  ): Promise<CustomerContact> =>
    apiClient
      .patch<CustomerContact>(`${base}/${customerId}/contacts/${id}`, payload)
      .then((r) => r.data),
  removeContact: (customerId: string, id: string): Promise<void> =>
    apiClient.delete(`${base}/${customerId}/contacts/${id}`).then(() => undefined),
  restoreContact: (customerId: string, id: string): Promise<CustomerContact> =>
    apiClient
      .post<CustomerContact>(`${base}/${customerId}/contacts/${id}/restore`)
      .then((r) => r.data),
};
